import { useEffect, RefObject } from 'react';

// TODO: Move to util or shared library later.
interface UseInfiniteScrollOptions {
  /**
   * Ref to the container element
   */
  containerRef: RefObject<HTMLElement>;
  /**
   * Whether there are more pages to load
   */
  hasNextPage?: boolean;
  /**
   * Whether the query is currently loading
   */
  isLoading?: boolean;
  /**
   * Function to fetch the next page
   */
  fetchNextPage?: () => void;
  /**
   * Number of items in the current list (used for dependency tracking)
   */
  itemCount?: number;
  /**
   * Root margin for intersection observer (default: '100px')
   */
  rootMargin?: string;
  /**
   * Intersection threshold (default: 0)
   */
  threshold?: number;
}

/**
 * Custom hook for implementing infinite scroll using Intersection Observer API.
 * Observes the last child element in the container and triggers fetchNextPage when it becomes visible.
 *
 * @param options Configuration options for the infinite scroll behavior
 */
export const useInfiniteScroll = ({
  containerRef,
  hasNextPage,
  isLoading,
  fetchNextPage,
  itemCount = 0,
  rootMargin = '100px',
  threshold = 0,
}: UseInfiniteScrollOptions) => {
  useEffect(() => {
    if (!containerRef.current || !hasNextPage || isLoading || !fetchNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      },
      {
        rootMargin,
        threshold,
      }
    );

    const lastChild = containerRef.current.lastElementChild;
    if (lastChild) {
      observer.observe(lastChild);
    }

    return () => {
      observer.disconnect();
    };
  }, [containerRef, hasNextPage, isLoading, fetchNextPage, itemCount, rootMargin, threshold]);
};
